/**
 * UTM Parameter Capture Utility
 * Extracts and stores UTM parameters from URL for analytics
 */

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  // Ad tracking parameters
  ad_platform?: string; // Reddit, YouTube, Instagram, etc.
  ad_campaign_id?: string;
  ad_creative_id?: string;
}

/**
 * Extract UTM parameters and ad tracking parameters from current URL
 */
export function captureUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search);

  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
    // Ad tracking parameters
    ad_platform: params.get('ad_platform') || undefined,
    ad_campaign_id: params.get('ad_campaign_id') || undefined,
    ad_creative_id: params.get('ad_creative_id') || undefined,
  };
}

/**
 * Store UTM params in sessionStorage for persistence across navigation
 */
export function storeUTMParams(params: UTMParams): void {
  try {
    sessionStorage.setItem('quickstart_utm', JSON.stringify(params));
  } catch (error) {
    console.warn('Failed to store UTM params:', error);
  }
}

/**
 * Retrieve stored UTM params from sessionStorage
 */
export function getStoredUTMParams(): UTMParams | null {
  try {
    const stored = sessionStorage.getItem('quickstart_utm');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to retrieve stored UTM params:', error);
    return null;
  }
}

/**
 * Get UTM params - from URL if present, otherwise from storage
 */
export function getUTMParams(): UTMParams {
  const urlParams = captureUTMParams();

  // If URL has UTM params, store them and return
  if (Object.values(urlParams).some(v => v !== undefined)) {
    storeUTMParams(urlParams);
    return urlParams;
  }

  // Otherwise return stored params or empty object
  return getStoredUTMParams() || {};
}

/**
 * Clear stored UTM params
 */
export function clearUTMParams(): void {
  try {
    sessionStorage.removeItem('quickstart_utm');
  } catch (error) {
    console.warn('Failed to clear UTM params:', error);
  }
}

